import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { PiPencilSimpleLineBold, PiUserCheck } from "react-icons/pi";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdOutlineCalendarMonth } from "react-icons/md";
import { IoTimeOutline } from "react-icons/io5";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { TbCalendarStats } from "react-icons/tb";
import { TbRefreshAlert } from "react-icons/tb";
import { MdOutlineMonetizationOn } from "react-icons/md";
import ButtonCom from "../../components/button/button";
import { ToastContainer } from "react-toastify";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchBranchData, initBranchState } from "../../redux/slices/branchSlice";
import { fetchDepartmentData, initDepartState } from "../../redux/slices/departmentSlice.js";
import {
  commonGetData,
  commonGetDataFourth,
  commonSaveData,
  initCommonState,
} from "../../redux/slices/commonSlice";
import {
  deleteLeaveMainPath,
  getLeaveMain,
  insertLeaveMainPath,
  getStaffLeaveListPath,
  assignLeaveEmployee,
} from "../../services/config";
import Widgets from "../../components/Widgets";
import WidgetsIcon from "../../components/Widgets/icon";
import { HasPermission } from "../../services/hasPermission";
import AssignEmployee from "../../components/AssignEmployee";
import { useToast } from "../../CustomToast/ToastContext.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const getFullMonthName = (m) => {
  const n = parseInt(m);
  return !m || isNaN(n) || n < 1 || n > 12 ? "" : MONTHS[n - 1];
};


const buildStatsFromApiList = (list) => {
  const stats  = {};
  const counts = {};
  (list || []).forEach((m) => {
    const d = m.leaveData || {};
    stats[m.moduleId] = {
      loading:          false,
      periodFrom:       d.periodFrom       || "",
      periodTo:         d.periodTo         || "",
      leaveTypesLabel:  d.leaveTypes       || "",
      totalDays:        d.totalDays        ?? 0,
      casualLeave:      d.casualLeave,
      sickLeave:        d.sickLeave,
      otherLeaves:      d.otherLeaves,
      // FIX: Check for combined rules instead of old carry forward flag
      // If combinedPolicy exists and is "1", that means combined rules are enabled
      carryForward:     d.combinedPolicy === "1",  // ← Changed this line
      encashmentEnabled:d.leaveEncashment  === "1",
    };
    counts[m.moduleId] = d.staffCount ?? 0;
  });
  return { stats, counts };
};

// ─── Theme ────────────────────────────────────────────────────────────────────
const buildTheme = (isDark) => ({
  bgPage:          isDark ? "#0D0B19" : "#F5F7FA",
  bgCard:          isDark ? "#19152C" : "#FFFFFF",
  bgInput:         isDark ? "#161421" : "#FFFFFF",
  textPrimary:     isDark ? "#E3E2EB" : "#1A1A2E",
  textMuted:       isDark ? "#9998A2" : "#6B6B8A",
  textPlaceholder: isDark ? "#9998A2" : "#8A8AA8",
  border:          isDark ? "#2D2A45" : "#E0E0F0",
  borderInput:     isDark ? "#3D3A58" : "#E0E0F0",
  borderFocus:     isDark ? "#8660F8" : "#6C63FF",
  focusShadow:     isDark ? "rgba(134,96,248,0.18)" : "rgba(108,99,255,0.15)",
  primary:         isDark ? "#8660F8" : "#6C63FF",
  gradient:        isDark ? "linear-gradient(135deg,#8660F8,#7550E8)" : "linear-gradient(135deg,#6C63FF,#5A52D5)",
  success:         isDark ? "#22C55E" : "#16A34A",
  successBg:       isDark ? "rgba(34,197,94,0.12)"  : "rgba(22,163,74,0.08)",
  successBrd:      isDark ? "rgba(34,197,94,0.35)"  : "rgba(22,163,74,0.35)",
  danger:          "#EF4444",
  dangerBg:        isDark ? "rgba(239,68,68,0.12)"  : "rgba(239,68,68,0.08)",
  dangerBrd:       isDark ? "rgba(239,68,68,0.4)"   : "rgba(239,68,68,0.3)",
  warning:         isDark ? "#F59E0B" : "#D97706",
  warningBg:       isDark ? "rgba(245,158,11,0.12)" : "rgba(217,119,6,0.08)",
  warningBrd:      isDark ? "rgba(245,158,11,0.4)"  : "rgba(217,119,6,0.35)",
  info:            "#37AC7E",
  infoBg:          isDark ? "rgba(55,172,126,0.12)" : "rgba(55,172,126,0.08)",
  infoBrd:         isDark ? "rgba(55,172,126,0.4)"  : "rgba(55,172,126,0.35)",
  carry:           isDark ? "#F59E0B" : "#B45309",
  carryBg:         isDark ? "rgba(245,158,11,0.12)" : "rgba(180,83,9,0.07)",
  carryBrd:        isDark ? "rgba(245,158,11,0.35)" : "rgba(180,83,9,0.25)",
  encash:          isDark ? "#06B6D4" : "#0E7490",
  encashBg:        isDark ? "rgba(6,182,212,0.12)"  : "rgba(14,116,144,0.07)",
  encashBrd:       isDark ? "rgba(6,182,212,0.35)"  : "rgba(14,116,144,0.25)",
  typeRowBg:       isDark ? "rgba(134,96,248,0.08)" : "rgba(108,99,255,0.05)",
  typeRowBrd:      isDark ? "rgba(134,96,248,0.2)"  : "rgba(108,99,255,0.12)",
  typeBadgeBg:     isDark ? "rgba(134,96,248,0.15)" : "rgba(108,99,255,0.1)",
  emptyBg:         isDark ? "rgba(255,255,255,0.03)": "rgba(108,99,255,0.03)",
  addHoverBg:      isDark ? "#1E1932" : "#F8FAFF",
  cardShadow:      isDark ? "0 2px 16px rgba(0,0,0,0.4)" : "0 2px 10px rgba(0,0,0,0.05)",
  cardHoverShadow: isDark ? "0 8px 24px rgba(134,96,248,0.18)" : "0 6px 20px rgba(108,99,255,0.11)",
  dialogShadow:    isDark ? "0 24px 60px rgba(0,0,0,0.65)" : "0 16px 48px rgba(0,0,0,0.1)",
  primaryShadow:   isDark ? "0 4px 14px rgba(134,96,248,0.4)" : "0 4px 12px rgba(108,99,255,0.32)",
  dangerShadow:    "0 4px 12px rgba(239,68,68,0.28)",
  successShadow:   "0 3px 10px rgba(34,197,94,0.28)",
  shimmerColor:    isDark ? "rgba(255,255,255,0.04)" : "rgba(108,99,255,0.03)",
});

// ─── Static styles ────────────────────────────────────────────────────────────
const STATIC_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
.lm-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:16px;max-width:1600px;margin:0 auto}
@media(max-width:1200px){.lm-grid{grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:14px}}
@media(max-width:768px){.lm-grid{grid-template-columns:1fr;gap:12px}}
.lm-card{position:relative;border-radius:12px;overflow:hidden;font-family:'Outfit',sans-serif;transition:box-shadow .28s ease}
.lm-card:hover{box-shadow:var(--lm-card-hover-shadow,0 8px 24px rgba(134,96,248,0.18))}
.lm-shimmer{position:absolute;top:0;left:0;width:100%;height:100%;z-index:1;pointer-events:none;transform:translateX(-100%);transition:transform .55s ease}
.lm-card:hover .lm-shimmer{transform:translateX(100%)}
.lm-sep{border-bottom-width:1px;border-bottom-style:solid}
.lm-add-card{border-radius:12px;border-width:2px;border-style:dashed;min-height:290px;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;font-family:'Outfit',sans-serif;transition:border-color .22s,background .22s,box-shadow .22s}
.lm-type-row{display:flex;justify-content:space-between;align-items:center;padding:4px 8px;border-radius:5px;margin-bottom:4px;border-width:1px;border-style:solid;transition:opacity .15s}
.lm-type-row:last-child{margin-bottom:0}
.lm-type-row:hover{opacity:0.82}
.lm-btn{display:flex;flex-direction:column;align-items:center;gap:2px;padding:7px 6px;border:none;border-radius:7px;cursor:pointer;font-family:'Outfit',sans-serif;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;color:#fff;transition:opacity .18s ease;position:relative;overflow:hidden}
.lm-btn::after{content:'';position:absolute;inset:0;background:rgba(255,255,255,0);transition:background .18s}
.lm-btn:hover::after{background:rgba(255,255,255,.1)}
.lm-btn:hover{opacity:0.88}
.lm-btn:active{opacity:0.72}
.lm-btn-link{text-decoration:none}
.lm-skel{border-radius:4px;animation:lmSkelPulse 1.5s ease-in-out infinite}
@keyframes lmSkelPulse{0%,100%{opacity:.45}50%{opacity:.9}}
.lm-in{animation:lmFadeIn .3s ease-out both}
@keyframes lmFadeIn{from{opacity:0}to{opacity:1}}
.lm-scroll::-webkit-scrollbar{width:4px}
.lm-scroll::-webkit-scrollbar-track{background:transparent}
.lm-dialog .form-control{font-family:'Outfit',sans-serif!important;font-size:12px!important;border-radius:8px!important;padding:9px 12px!important;transition:border-color .2s,box-shadow .2s!important}
.MuiDialog-root{position:fixed!important;z-index:1300!important}
.MuiBackdrop-root{position:fixed!important;top:0!important;left:0!important;right:0!important;bottom:0!important}
.SettingBody{position:relative!important;z-index:1!important}
.lm-meta-strip{display:flex;gap:6px;flex-wrap:wrap;padding:8px 14px;border-bottom-width:1px;border-bottom-style:solid}
.lm-meta-chip{display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:5px;font-family:'Outfit',sans-serif;font-size:11px;font-weight:600;border-width:1px;border-style:solid}
`;

if (typeof document !== "undefined" && !document.getElementById("lm-static-styles")) {
  const el = document.createElement("style");
  el.id = "lm-static-styles";
  el.textContent = STATIC_STYLES;
  document.head.appendChild(el);
}

const useThemeStyles = (isDark) => {
  const styleRef = useRef(null);
  useEffect(() => {
    const t = buildTheme(isDark);
    const css = `
      .lm-card{background:${t.bgCard};border:1px solid ${t.border};box-shadow:${t.cardShadow};--lm-card-hover-shadow:${t.cardHoverShadow}}
      .lm-shimmer{background:linear-gradient(90deg,transparent,${t.shimmerColor},transparent)}
      .lm-sep{border-bottom-color:${t.border}}
      .lm-meta-strip{border-bottom-color:${t.border}}
      .lm-add-card{background:${t.bgCard};border-color:${t.border}}
      .lm-add-card:hover{border-color:${t.primary};background:${t.addHoverBg};box-shadow:${t.cardHoverShadow}}
      .lm-type-row{background:${t.typeRowBg};border-color:${t.typeRowBrd}}
      .lm-skel{background:${t.border}}
      .lm-scroll::-webkit-scrollbar-thumb{background:${t.border};border-radius:4px}
      .lm-dialog .form-control{background-color:${t.bgInput}!important;color:${t.textPrimary}!important;border:1px solid ${t.borderInput}!important}
      .lm-dialog .form-control::placeholder{color:${t.textPlaceholder}!important;opacity:1!important}
      .lm-dialog .form-control:focus{outline:none!important;border-color:${t.borderFocus}!important;box-shadow:0 0 0 3px ${t.focusShadow}!important;background-color:${t.bgInput}!important}
    `;
    if (!styleRef.current) {
      const el = document.createElement("style");
      el.id = "lm-theme-styles";
      document.head.appendChild(el);
      styleRef.current = el;
    }
    styleRef.current.textContent = css;
  }, [isDark]);
};

// ─── SkeletonCard ─────────────────────────────────────────────────────────────
const SkeletonCard = React.memo(({ gradient }) => (
  <div className="lm-card" style={{ minHeight: "290px" }}>
    <div style={{ height: "3px", background: gradient }} />
    <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
      <div className="lm-skel" style={{ width: "36px", height: "36px", borderRadius: "9px" }} />
      <div className="lm-skel" style={{ width: "60%", height: "13px" }} />
      <div className="lm-skel" style={{ width: "40%", height: "11px" }} />
      <div className="lm-skel" style={{ width: "100%", height: "44px", borderRadius: "8px" }} />
      <div className="lm-skel" style={{ width: "100%", height: "80px", borderRadius: "8px" }} />
      <div style={{ display: "flex", gap: "6px" }}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="lm-skel" style={{ flex: 1, height: "30px", borderRadius: "6px" }} />
        ))}
      </div>
    </div>
  </div>
));

// ─── ModuleCard ───────────────────────────────────────────────────────────────
// Uses data already present in leaveData from the single /getLeaveMain API call.
// No secondary fetches needed.
const ModuleCard = React.memo(({ module, stats, assignedCount, t, onAssign, onDelete }) => {
  if (!stats || stats.loading) return <SkeletonCard gradient={t.gradient} />;

  const fromMonth       = getFullMonthName(stats.periodFrom);
  const toMonth         = getFullMonthName(stats.periodTo);
  const carryForward    = stats.carryForward;
  const encashEnabled   = stats.encashmentEnabled;
  const leaveTypesLabel = stats.leaveTypesLabel || "";
  const totalDays       = stats.totalDays || 0;
  const casualLeave     = stats.casualLeave;
  const sickLeave       = stats.sickLeave;
  const otherLeaves     = stats.otherLeaves;

  // Build leave rows from what's available in leaveData
  const leaveRows = [
    casualLeave  != null && { label: "Casual Leave",  value: casualLeave },
    sickLeave    != null && { label: "Sick Leave",    value: sickLeave },
    otherLeaves  != null && { label: "Other Leaves",  value: otherLeaves },
  ].filter(Boolean);

  return (
    <div className="lm-card lm-in">
      <div className="lm-shimmer" />
      <div style={{ height: "3px", background: t.gradient }} />

      {/* Header */}
      <div className="lm-sep" style={{ padding: "12px 14px 10px" }}>
        <div
          style={{
            width: "36px", height: "36px", borderRadius: "9px",
            background: t.gradient, display: "flex", alignItems: "center",
            justifyContent: "center", boxShadow: t.primaryShadow, marginBottom: "9px",
          }}
        >
          <TbCalendarStats style={{ fontSize: "17px", color: "#fff" }} />
        </div>
        <p style={{ margin: "0 0 7px", fontFamily: "'Outfit',sans-serif", fontSize: "13px", fontWeight: 600, color: t.textPrimary, lineHeight: 1.3 }}>
          {module.moduleName || module.name || "Unnamed Module"}
        </p>
        {fromMonth && toMonth ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "3px 8px", borderRadius: "5px", background: t.infoBg, border: `1px solid ${t.infoBrd}`, fontFamily: "'Outfit',sans-serif", fontSize: "11px", fontWeight: 600, color: t.info }}>
            <MdOutlineCalendarMonth style={{ fontSize: "11px" }} />{fromMonth} – {toMonth}
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "3px 8px", borderRadius: "5px", background: t.warningBg, border: `1px solid ${t.warningBrd}`, fontFamily: "'Outfit',sans-serif", fontSize: "11px", fontWeight: 600, color: t.warning }}>
            <IoTimeOutline style={{ fontSize: "11px" }} />Period Not Set
          </span>
        )}
      </div>

      {/* Stats row */}
      <div className="lm-sep" style={{ padding: "10px 14px", display: "grid", gridTemplateColumns: "1fr auto", gap: "10px", alignItems: "center" }}>
        <div>
          <p style={{ margin: "0 0 2px", fontFamily: "'Outfit',sans-serif", fontSize: "10px", fontWeight: 500, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Leave Types</p>
          <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
            <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "22px", fontWeight: 700, color: t.primary }}>
              {leaveTypesLabel || "—"}
            </span>
            {totalDays > 0 && (
              <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "11px", fontWeight: 400, color: t.textMuted }}>
                · {totalDays}d total
              </span>
            )}
          </div>
        </div>
        <div style={{ padding: "7px 14px", borderRadius: "8px", background: t.successBg, border: `1px solid ${t.successBrd}`, textAlign: "center", minWidth: "70px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "3px", marginBottom: "1px" }}>
            <HiOutlineUserGroup style={{ fontSize: "10px", color: t.success }} />
            <p style={{ margin: 0, fontFamily: "'Outfit',sans-serif", fontSize: "10px", fontWeight: 500, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Assigned</p>
          </div>
          <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: "20px", fontWeight: 700, color: t.success, lineHeight: 1 }}>
            {assignedCount}
          </div>
        </div>
      </div>

      {/* Carry-forward & Encashment meta strip */}
  
{/* {(carryForward || encashEnabled) && (
  <div className="lm-meta-strip" style={{ borderBottomColor: t.border }}>
    {carryForward && (
      <span className="lm-meta-chip" style={{ background: t.carryBg, borderColor: t.carryBrd, color: t.carry }}>
        <TbRefreshAlert style={{ fontSize: "12px" }} />Combined Rules On
      </span>
    )}
    {encashEnabled && (
      <span className="lm-meta-chip" style={{ background: t.encashBg, borderColor: t.encashBrd, color: t.encash }}>
        <MdOutlineMonetizationOn style={{ fontSize: "12px" }} />Encashment On
      </span>
    )}
  </div>
)} */}

      {/* Leave breakdown rows */}
      <div className="lm-sep" style={{ padding: "10px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "7px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div style={{ width: "3px", height: "10px", background: t.gradient, borderRadius: "2px" }} />
            <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "10px", fontWeight: 500, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Leave Breakdown</span>
          </div>
        </div>
        {leaveRows.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {leaveRows.map((row, idx) => (
              <div key={idx} className="lm-type-row">
                <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "11px", fontWeight: 500, color: t.textPrimary }}>
                  {row.label}
                </span>
                <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "11px", fontWeight: 700, color: t.primary, padding: "1px 7px", borderRadius: "4px", background: t.typeBadgeBg }}>
                  {row.value}d
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: "10px 8px", textAlign: "center", borderRadius: "6px", background: t.emptyBg, border: `1px dashed ${t.border}` }}>
            <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "11px", color: t.textMuted, fontStyle: "italic" }}>
              No leave types configured yet
            </span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ padding: "10px 12px 12px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "6px" }}>
        {HasPermission("Leave Settings", "create") && (
          <button
            className="lm-btn"
            style={{ background: "linear-gradient(135deg,#22C55E,#16A34A)", boxShadow: t.successShadow }}
            onClick={(e) => { e.stopPropagation(); onAssign(module); }}
          >
            <PiUserCheck style={{ fontSize: "14px" }} /><span>Assign</span>
          </button>
        )}
        {HasPermission("Leave Settings", "edit") && (
          <Link to={`/leave-attendance/${module.moduleId}`} className="lm-btn-link" onClick={(e) => e.stopPropagation()}>
            <button className="lm-btn" style={{ width: "100%", background: t.gradient, boxShadow: t.primaryShadow }}>
              <PiPencilSimpleLineBold style={{ fontSize: "14px" }} /><span>Edit</span>
            </button>
          </Link>
        )}
        {HasPermission("Leave Settings", "delete") && (
          <button
            className="lm-btn"
            style={{ background: "linear-gradient(135deg,#EF4444,#DC2626)", boxShadow: t.dangerShadow }}
            onClick={(e) => { e.stopPropagation(); onDelete(module.moduleId); }}
          >
            <RiDeleteBinLine style={{ fontSize: "14px" }} /><span>Delete</span>
          </button>
        )}
      </div>
    </div>
  );
});

// ─── AddCard ──────────────────────────────────────────────────────────────────
const AddCard = React.memo(({ t, onClick }) => (
  <div className="lm-add-card" onClick={onClick}>
    <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: t.gradient, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "12px", boxShadow: t.primaryShadow }}>
      <AddIcon style={{ fontSize: "22px", color: "#fff" }} />
    </div>
    <p style={{ margin: "0 0 5px", fontFamily: "'Outfit',sans-serif", fontSize: "13px", fontWeight: 600, color: t.textPrimary }}>Create New Module</p>
    <p style={{ margin: 0, fontFamily: "'Outfit',sans-serif", fontSize: "11px", color: t.textMuted, textAlign: "center", maxWidth: "150px", lineHeight: 1.5 }}>
      Set up a new leave management module
    </p>
  </div>
));

// ════════════════════════════════════════════════════════════════════════════════
const LeaveModule = () => {
  const navigate = useNavigate();
  const toast    = useToast();
  const user     = JSON.parse(localStorage.getItem("user_data")) || {};

  const dispatch        = useDispatch();
  const commonStore     = useSelector((s) => s.common);
  const branchStore     = useSelector((s) => s.branch);
  const departmentStore = useSelector((s) => s.department);
  const themeStore      = useSelector((s) => s.theme);

  const isDark = themeStore?.theme === "dark" || !themeStore?.theme;
  useThemeStyles(isDark);
  const t = useMemo(() => buildTheme(isDark), [isDark]);

  // ── Core state ──────────────────────────────────────────────────────────────
  const [isSave, setIsSave]                         = useState(false);
  const [moduleList, setModuleList]                 = useState([]);
  const [branchList, setBranchList]                 = useState([]);
  const [departmentList, setDepartmentList]         = useState([]);
  const [showDelete, setShowDelete]                 = useState(false);
  const [deleteId, setDeleteId]                     = useState(null);
  const [showCreate, setShowCreate]                 = useState(false);
  const [showAssign, setShowAssign]                 = useState(false);
  const [moduleName, setModuleName]                 = useState("");
  const [employeeList, setEmployeeList]             = useState([]);
  const [filterEmployeeList, setFilterEmployeeList] = useState([]);
  const [leave_id, set_leave_id]                    = useState("");
  const [selectedUsers, setSelectedUsers]           = useState([]);
  const [selectedUsersList, setSelectedUsersList]   = useState([]);
  const [searchQuery, setSearchQuery]               = useState("");
  const [outletName, setOutletName]                 = useState("");
  const [filterState, setFilterState]               = useState({ branchId: "", departmentId: "" });
  const [isAssigning, setIsAssigning]               = useState(false);

  // moduleStats: derived directly from leaveData in the API response — no extra fetches
  const [moduleStats, setModuleStats]       = useState({});
  // assignedCounts: updated locally after assign; starts at 0 (no count API needed on load)
  const [assignedCounts, setAssignedCounts] = useState({});

  // ── Refs ────────────────────────────────────────────────────────────────────
  // Only one ref needed now — to track pending save/delete/assign operations
  const pendingOpRef = useRef(null);

  // Track previous filter to avoid redundant employee-list fetches
  const prevFilterRef = useRef({ branchId: "", departmentId: "" });

  const isSelectAll = selectedUsers?.length === employeeList?.length && employeeList?.length > 0;

  const btnCancel  = useMemo(() => ({ fontFamily: "'Outfit',sans-serif", fontSize: "12px", fontWeight: 600, borderRadius: "8px", cursor: "pointer", padding: "8px 20px", background: "transparent", border: `1px solid ${t.border}`, color: t.textPrimary }), [t]);
  const btnPrimary = useMemo(() => ({ fontFamily: "'Outfit',sans-serif", fontSize: "12px", fontWeight: 600, borderRadius: "8px", cursor: "pointer", padding: "8px 20px", border: "none", background: t.gradient, color: "#fff", boxShadow: t.primaryShadow }), [t]);
  const btnDanger  = useMemo(() => ({ fontFamily: "'Outfit',sans-serif", fontSize: "12px", fontWeight: 600, borderRadius: "8px", cursor: "pointer", padding: "8px 20px", border: "none", background: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#fff", boxShadow: t.dangerShadow }), [t]);
  const dlgPaper   = useMemo(() => ({ backgroundColor: t.bgCard, border: `1px solid ${t.border}`, borderRadius: "12px", color: t.textPrimary, fontFamily: "'Outfit',sans-serif", boxShadow: t.dialogShadow }), [t]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  // Update assigned count locally — no API call needed
  const setCount = useCallback((moduleId, count) => {
    setAssignedCounts((prev) => ({ ...prev, [moduleId]: count }));
  }, []);

  // ── Data fetching ────────────────────────────────────────────────────────────

  const getLeaveModuleList = useCallback(() => {
    dispatch(commonGetDataFourth(`${getLeaveMain}${user.user_id}`));
    dispatch(fetchBranchData());
    dispatch(fetchDepartmentData(user.user_id));
  }, [user.user_id, dispatch]);

  const getEmployeeList = useCallback((leaveId) => {
    if (!leaveId) return;
    dispatch(commonGetData(getStaffLeaveListPath(user.user_id, leaveId, filterState.branchId, filterState.departmentId)));
  }, [user.user_id, filterState.branchId, filterState.departmentId, dispatch]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleDeleteClose  = useCallback(() => { setShowDelete(false); setDeleteId(null); }, []);
  const handleDelete       = useCallback((id) => { setDeleteId(id); setShowDelete(true); }, []);
  const handleCreateClose  = useCallback(() => { setShowCreate(false); setModuleName(""); }, []);
  const handleCreateOpen   = useCallback(() => setShowCreate(true), []);

  const handleAssignClose = useCallback(() => {
    setShowAssign(false);
    setSelectedUsers([]);
    setSelectedUsersList([]);
    setEmployeeList([]);
    setFilterEmployeeList([]);
    setSearchQuery("");
    setFilterState({ branchId: "", departmentId: "" });
    prevFilterRef.current = { branchId: "", departmentId: "" };
    set_leave_id("");
    setOutletName("");
  }, []);

  const handleAssign = useCallback((moduleData) => {
    setSelectedUsers([]);
    setSelectedUsersList([]);
    setEmployeeList([]);
    setFilterEmployeeList([]);
    setSearchQuery("");
    setFilterState({ branchId: "", departmentId: "" });
    prevFilterRef.current = { branchId: "", departmentId: "" };
    set_leave_id(moduleData.moduleId);
    setOutletName(moduleData.moduleName);
    getEmployeeList(moduleData.moduleId);
    setShowAssign(true);
  }, [getEmployeeList]);

  const handleDeleteSubmit = useCallback(() => {
    if (!deleteId) { toast.error("Module ID is missing"); return; }
    pendingOpRef.current = "delete";
    dispatch(commonSaveData({ path: deleteLeaveMainPath, data: { userId: user.user_id, leaveId: deleteId } }));
  }, [dispatch, deleteId, user.user_id, toast]);

  const createLeaveModule = useCallback((e) => {
    e.preventDefault();
    if (!moduleName.trim()) { toast.error("Please enter a module name"); return; }
    pendingOpRef.current = "create";
    dispatch(commonSaveData({ path: insertLeaveMainPath, data: { userId: user.user_id, moduleName } }));
    setIsSave(true);
  }, [moduleName, dispatch, user.user_id, toast]);

  const handleAssignSubmit = useCallback((e) => {
    e.preventDefault();
    if (!selectedUsers?.length) { toast.error("Please select at least one employee"); return; }
    if (!leave_id) { toast.error("Module ID is missing"); return; }
    pendingOpRef.current = "assign";
    setIsAssigning(true);
    dispatch(commonSaveData({
      path: assignLeaveEmployee,
      data: { userId: user.user_id, staffId: selectedUsers.join(","), moduleId: leave_id },
    }));
  }, [selectedUsers, dispatch, user.user_id, leave_id, toast]);

  const selectUser = useCallback((event, userId, userObj) => {
    const checked = event.target.checked;
    setSelectedUsers((p) => checked ? [...p, userId] : p.filter((id) => id !== userId));
    setSelectedUsersList((p) => checked ? [...p, userObj] : p.filter((u) => u.staffId !== userObj.staffId));
  }, []);

  const SelectAll = useCallback((event) => {
    if (event.target.checked) {
      setSelectedUsers(employeeList.map((r) => r.staffId));
      setSelectedUsersList([...employeeList]);
    } else {
      setSelectedUsers([]);
      setSelectedUsersList([]);
    }
  }, [employeeList]);

  const handleInputChange = useCallback(({ target: { name, value } }) => {
    setFilterState((p) => ({ ...p, [name]: value }));
  }, []);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => { getLeaveModuleList(); }, [getLeaveModuleList]);

  // ── Module list received ────────────────────────────────────────────────────
  // Builds stats directly from leaveData in the response — ZERO secondary API calls.
  useEffect(() => {
    if (!commonStore.commonGetFourthSuccess) return;
    const list = commonStore.commonGetFourthData || [];
    setModuleList(list);
    const { stats, counts } = buildStatsFromApiList(list);
    setModuleStats(stats);
    // Only initialise counts for modules not already tracked (preserves post-assign counts)
    setAssignedCounts((prev) => {
      const next = { ...counts };
      Object.keys(prev).forEach((id) => { if (prev[id] !== 0) next[id] = prev[id]; });
      return next;
    });
    dispatch(initCommonState());
  }, [commonStore.commonGetFourthSuccess, commonStore.commonGetFourthData, dispatch]);

  // ── Save / Error handler ────────────────────────────────────────────────────
  useEffect(() => {
    if (commonStore.commonSuccess) {
      const op = pendingOpRef.current;
      pendingOpRef.current = null;
      toast.success(commonStore.commonMessage);

      if (op === "delete") {
        setShowDelete(false);
        setDeleteId(null);
        getLeaveModuleList();
        dispatch(initCommonState());
        return;
      }

      if (op === "assign") {
        setIsAssigning(false);
        setShowAssign(false);
        // Update count locally — no need to re-fetch the whole list
        if (leave_id) setCount(leave_id, selectedUsers.length);
        setSelectedUsers([]);
        setSelectedUsersList([]);
        setSearchQuery("");
        setFilterState({ branchId: "", departmentId: "" });
        prevFilterRef.current = { branchId: "", departmentId: "" };
        dispatch(initCommonState());
        return; // skip getLeaveModuleList — count already updated locally
      }

      // create
      handleCreateClose();
      if (op === "create" && isSave) {
        setIsSave(false);
        navigate(`/leave-attendance/${commonStore.commonData?.id}`);
      } else {
        getLeaveModuleList();
      }
      dispatch(initCommonState());
    }

    if (commonStore.commonError) {
      const op  = pendingOpRef.current;
      const msg = commonStore.commonMessage || "";
      const isDeleteOutcome =
        op === "delete" ||
        msg.includes("Leave Module Deleted successfully") ||
        msg.includes("Failed to update staff status") ||
        msg.includes("Leave Module not found for the given userId");

      if (isDeleteOutcome) {
        toast.success("Leave Module deleted successfully");
        setShowDelete(false);
        setDeleteId(null);
        getLeaveModuleList();
      } else {
        toast.error(msg || "Something went wrong");
      }
      pendingOpRef.current = null;
      setIsAssigning(false);
      dispatch(initCommonState());
    }

    if (departmentStore.depGetSuccess) { setDepartmentList(departmentStore.depGetData); dispatch(initDepartState()); }
    if (branchStore.brnGetSuccess)     { setBranchList(branchStore.brnGetData);         dispatch(initBranchState()); }
  }, [
    commonStore, departmentStore, branchStore,
    dispatch, getLeaveModuleList, isSave, navigate,
    toast, handleCreateClose, leave_id, selectedUsers, setCount,
  ]);

  // ── Employee list response ──────────────────────────────────────────────────
  // commonGetData is now ONLY used for the employee list (assign dialog).
  // The old moduleLeaves / countFetch branches are gone.
  useEffect(() => {
    if (!commonStore.commonGetSuccess) return;
    const data = commonStore.commonGetData || [];
    setEmployeeList(data);
    setFilterEmployeeList(data);
    const assigned = data.filter((v) => v.leaveAssignedStatus === "1" || v.leaveAssignedStatus === 1);
    setSelectedUsers(assigned.map((s) => s.staffId));
    setSelectedUsersList(assigned);
    dispatch(initCommonState());
  }, [commonStore.commonGetSuccess, commonStore.commonGetData, dispatch]);

  // ── Filter change → refetch employee list (only when assign dialog is open) ─
  useEffect(() => {
    if (!showAssign || !leave_id) return;
    // Skip if filter didn't actually change
    if (
      prevFilterRef.current.branchId     === filterState.branchId &&
      prevFilterRef.current.departmentId === filterState.departmentId
    ) return;
    prevFilterRef.current = { ...filterState };
    getEmployeeList(leave_id);
  }, [filterState, leave_id, showAssign, getEmployeeList]);

  // ── Search filter (client-side, no API call) ────────────────────────────────
  useEffect(() => {
    if (searchQuery && filterEmployeeList.length > 0) {
      const q = searchQuery.toLowerCase();
      setEmployeeList(
        filterEmployeeList.filter(
          (v) => v.full_name?.toLowerCase().includes(q) || v.employee_id?.toLowerCase().includes(q)
        )
      );
    } else {
      setEmployeeList(filterEmployeeList);
    }
  }, [searchQuery, filterEmployeeList]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={2000} />

      <AssignEmployee
        isOpen={showAssign}
        closeOpenDialog={handleAssignClose}
        submitData={handleAssignSubmit}
        isSelectAll={isSelectAll}
        SelectAll={SelectAll}
        selectedUsersList={selectedUsersList}
        employeeList={employeeList}
        selectedUsers={selectedUsers}
        selectUser={selectUser}
        branchList={branchList}
        departmentList={departmentList}
        state={filterState}
        handleInputChange={handleInputChange}
        getData={getEmployeeList}
        searchQuery={searchQuery}
        searchEmployees={(val) => setSearchQuery(val)}
        outletName={outletName}
        dataList={filterEmployeeList}
        isLoading={isAssigning}
      />

      {/* Create Dialog */}
      <Dialog open={showCreate} onClose={handleCreateClose} maxWidth="xs" fullWidth className="lm-dialog" PaperProps={{ style: dlgPaper }}>
        <DialogTitle style={{ backgroundColor: t.bgCard, borderBottom: `1px solid ${t.border}`, padding: "14px 18px", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "30px", height: "30px", borderRadius: "7px", background: t.gradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: t.primaryShadow }}>
            <AddIcon style={{ color: "#fff", fontSize: "16px" }} />
          </div>
          <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "14px", fontWeight: 600, color: t.textPrimary, flex: 1 }}>Create Leave Module</span>
          <button onClick={handleCreateClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: "18px", lineHeight: 1, padding: "2px 6px", borderRadius: "6px" }}>✕</button>
        </DialogTitle>
        <DialogContent style={{ backgroundColor: t.bgCard, padding: "16px 18px" }}>
          <p style={{ fontFamily: "'Outfit',sans-serif", fontSize: "11px", fontWeight: 500, color: t.textMuted, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Module Name <span style={{ color: t.danger }}>*</span>
          </p>
          <Form.Control
            type="text"
            placeholder="e.g. Annual Leave 2025"
            value={moduleName}
            onChange={(e) => setModuleName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createLeaveModule(e); }}
          />
          <p style={{ fontFamily: "'Outfit',sans-serif", fontSize: "11px", color: t.textMuted, margin: "8px 0 0", lineHeight: 1.5 }}>
            After creating, you'll be taken to the module settings page to configure leave types and periods.
          </p>
        </DialogContent>
        <DialogActions style={{ backgroundColor: t.bgCard, borderTop: `1px solid ${t.border}`, padding: "12px 18px", justifyContent: "flex-end", gap: "10px" }}>
          <button style={btnCancel} onClick={handleCreateClose}>Cancel</button>
          <button style={btnPrimary} onClick={createLeaveModule}>Create Module</button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDelete} onClose={handleDeleteClose} maxWidth="xs" fullWidth PaperProps={{ style: dlgPaper }}>
        <DialogTitle style={{ backgroundColor: t.bgCard, borderBottom: `1px solid ${t.border}`, padding: "14px 18px", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "30px", height: "30px", borderRadius: "7px", background: t.dangerBg, border: `1px solid ${t.dangerBrd}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RiDeleteBinLine style={{ color: t.danger, fontSize: "15px" }} />
          </div>
          <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "14px", fontWeight: 600, color: t.textPrimary, flex: 1 }}>Delete Leave Module</span>
          <button onClick={handleDeleteClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: t.textMuted, fontSize: "18px", lineHeight: 1, padding: "2px 6px", borderRadius: "6px" }}>✕</button>
        </DialogTitle>
        <DialogContent style={{ backgroundColor: t.bgCard, padding: "16px 18px" }}>
          <p style={{ fontFamily: "'Outfit',sans-serif", fontSize: "12px", lineHeight: 1.7, color: t.textPrimary, margin: "0 0 12px" }}>
            Are you sure you want to delete this leave module? This action will permanently remove all associated data and cannot be undone.
          </p>
          <div style={{ padding: "10px 12px", borderRadius: "7px", background: t.dangerBg, border: `1px solid ${t.dangerBrd}`, display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "14px", flexShrink: 0 }}>⚠️</span>
            <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: "12px", color: t.textMuted }}>This will affect all employees assigned to this module.</span>
          </div>
        </DialogContent>
        <DialogActions style={{ backgroundColor: t.bgCard, borderTop: `1px solid ${t.border}`, padding: "12px 18px", justifyContent: "flex-end", gap: "10px" }}>
          <button style={btnCancel} onClick={handleDeleteClose}>Cancel</button>
          <button style={btnDanger} onClick={handleDeleteSubmit}>Delete Module</button>
        </DialogActions>
      </Dialog>

      {/* Main */}
      <div className="SettingBody">
        <div className="SettingBody-card mt-3">
          <Widgets
            icon={
              <WidgetsIcon
                icon={<Link to="/setting/" className="icon-color"><ArrowBackIcon /></Link>}
                title="Leave Module"
              />
            }
            button={
              HasPermission("Leave Settings", "create") && (
                <ButtonCom
                  type="button"
                  class_name="universal-btn border-radius-8px"
                  text="Create Leave Module"
                  url_or_onclick={handleCreateOpen}
                  icon={<AddIcon />}
                  width=""
                />
              )
            }
            body={
              <div
                className="lm-scroll"
                style={{ padding: "20px 18px", backgroundColor: t.bgPage, minHeight: "calc(100vh - 200px)", overflowY: "auto" }}
              >
                <div className="lm-grid">
                  {Array.isArray(moduleList) && moduleList.length > 0
                    ? moduleList.map((module, idx) => (
                        <ModuleCard
                          key={module.moduleId || idx}
                          module={module}
                          stats={moduleStats[module.moduleId]}
                          assignedCount={assignedCounts[module.moduleId] ?? 0}
                          t={t}
                          onAssign={handleAssign}
                          onDelete={handleDelete}
                        />
                      ))
                    : (
                        <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "60px 20px" }}>
                          <div style={{ width: "56px", height: "56px", margin: "0 auto 14px", borderRadius: "50%", background: t.gradient, opacity: 0.4, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <TbCalendarStats style={{ fontSize: "28px", color: "#fff" }} />
                          </div>
                          <p style={{ fontFamily: "'Outfit',sans-serif", fontSize: "13px", color: t.textMuted, margin: 0 }}>
                            No leave modules found. Click "Create Leave Module" to add one.
                          </p>
                        </div>
                      )
                  }
                  {HasPermission("Leave Settings", "create") && (
                    <AddCard t={t} onClick={handleCreateOpen} />
                  )}
                </div>
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default React.memo(LeaveModule);